import {
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react'
import { type BulkInviteRowDetail, type BulkInviteRowStatus } from '@qse/api'
import moment from 'moment'

import { useModalFullScreen } from '../../modal-fullscreen-context'

interface RowDetailsTableProps {
  rows: BulkInviteRowDetail[]
  isLoading: boolean
  /** An outcome filter is active — an empty result then needs saying, not hiding. */
  isFiltered?: boolean
}

const ROW_STATUS_LABEL: Record<BulkInviteRowStatus, string> = {
  INVITED: 'Invited',
  ACCEPTED: 'Accepted',
  REVOKED: 'Revoked',
  EMAIL_NOT_SENT: 'Email not sent',
  FAILED: 'Failed',
  INVALID: 'Invalid',
  ALREADY_INVITED: 'Already invited',
  ALREADY_REGISTERED: 'Already registered',
  DELETED: 'Deleted',
}

/**
 * Colour carries the distinction the labels alone don't: red is "we tried and it
 * broke", amber is "we deliberately left this row alone", gray is "the admin took
 * it out themselves". Only FAILED and INVALID are red — an already-invited address
 * or an admin-removed row is an outcome, not a fault.
 */
const ROW_STATUS_CLASS: Record<BulkInviteRowStatus, string> = {
  INVITED: 'text-blue-600 dark:text-blue-400',
  ACCEPTED: 'text-green-600 dark:text-green-400',
  REVOKED: 'text-gray-500 dark:text-gray-400',
  EMAIL_NOT_SENT: 'text-amber-600 dark:text-amber-400',
  FAILED: 'text-red-500 dark:text-red-400',
  INVALID: 'text-red-500 dark:text-red-400',
  ALREADY_INVITED: 'text-amber-600 dark:text-amber-400',
  ALREADY_REGISTERED: 'text-amber-600 dark:text-amber-400',
  DELETED: 'text-gray-500 dark:text-gray-400',
}

export default function RowDetailsTable({ rows, isLoading, isFiltered }: RowDetailsTableProps) {
  // In the expanded modal (the History tab's whole point of expanding), let the
  // table take the viewport instead of the 16rem cap.
  const isFullScreen = useModalFullScreen()

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="sm" />
      </div>
    )
  }

  if (!rows.length) {
    if (!isFiltered) return null

    return (
      <div className="w-full rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-6 text-center text-sm text-gray-500 dark:text-[#AFAFAF]">
        No rows match this filter.
      </div>
    )
  }

  return (
    <div
      className={`w-full ${
        isFullScreen ? 'max-h-[65vh]' : 'max-h-64'
      } overflow-auto rounded-lg border border-gray-200 dark:border-[#2A2A2A]`}
    >
      <Table removeWrapper aria-label="Bulk invite row details" className="min-w-full">
        <TableHeader>
          <TableColumn>Email</TableColumn>
          <TableColumn>Name</TableColumn>
          <TableColumn>Status</TableColumn>
          <TableColumn>Invited</TableColumn>
          <TableColumn>Error</TableColumn>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.email}-${index}`}>
              <TableCell className="align-top text-sm">{row.email}</TableCell>
              <TableCell className="align-top whitespace-nowrap text-sm">
                {row.firstName || row.lastName
                  ? `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim()
                  : '—'}
              </TableCell>
              <TableCell className="align-top whitespace-nowrap">
                <span className={`text-sm font-medium ${ROW_STATUS_CLASS[row.status]}`}>
                  {ROW_STATUS_LABEL[row.status]}
                </span>
              </TableCell>
              <TableCell className="align-top whitespace-nowrap text-sm text-gray-500 dark:text-[#AFAFAF]">
                {row.invitedAt ? moment(row.invitedAt).format('MM/DD/YY HH:mm') : '—'}
              </TableCell>
              <TableCell className="align-top text-sm text-gray-700 dark:text-gray-300">
                {row.errorMessage || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
